// Dynamic route for CMS-authored articles (/blog/<slug>).
// The 17 hand-built article routes are static files and take precedence in the
// router, so this only ever serves posts published from the admin CMS.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Clock } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import BlockRenderer from "@/components/blog/BlockRenderer";
import { buildBlogHead } from "@/lib/blogMeta";
import { faqsFromBlocks } from "@/lib/blog/blocks";
import { getCmsPost } from "@/lib/blog-cms.functions";
import type { PublicPost } from "@/lib/blog/types";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";
import { CLUSTERS, type ClusterKey } from "@/lib/clusters";
import { publicAuthorName, publicAuthorUrl } from "@/lib/blog/author";
import { useT } from "@/lib/i18n/provider";
import { imageSrcSet, sizedImageUrl } from "@/lib/image-url";


const SITE = "https://truwe.in";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getCmsPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return post;
  },
  head: ({ params, loaderData }) => {
    const p = loaderData as PublicPost | undefined;
    if (!p) return { meta: [{ title: "Article | TruWe" }] };
    const faqs = p.faqs.length ? p.faqs : faqsFromBlocks(p.body);
    const head = buildBlogHead({
      slug: params.slug,
      title: p.metaTitle ?? `${p.title} | TruWe`,
      description: p.metaDescription ?? p.excerpt,
      ogTitle: p.ogTitle ?? p.title,
      ogDescription: p.ogDescription ?? p.metaDescription ?? p.excerpt,
      headline: p.title,
      breadcrumbName: p.title,
      datePublished: p.publishedAt,
      // Honest dateModified: only bumps when the article body actually changed.
      dateModified: p.contentUpdatedAt,
      articleType: "BlogPosting" as const,
      faqs,
      ...{ authorName: publicAuthorName(p.authorName) },
      ...(publicAuthorUrl(p.authorUrl) ? { authorUrl: publicAuthorUrl(p.authorUrl)! } : {}),
      ...(p.reviewerName ? { reviewerName: p.reviewerName } : {}),
      ...(p.reviewerCredentials ? { reviewerCredentials: p.reviewerCredentials } : {}),
      ...(p.reviewerUrl ? { reviewerUrl: p.reviewerUrl } : {}),
      ...(p.reviewedAt ? { reviewedAt: p.reviewedAt } : {}),
      ...(p.ogImage ?? p.heroImageUrl
        ? { ogImage: (p.ogImage ?? p.heroImageUrl) as string }
        : {}),
    });
    if (p.canonicalOverride) {
      head.links = [{ rel: "canonical", href: p.canonicalOverride }];
    }
    return head;
  },
  errorComponent: () => <BlogErrorShell />,
  notFoundComponent: () => <BlogNotFoundShell />,
  component: CmsArticle,
});

function BlogErrorShell() {
  const t = useT();
  return (
    <ArticleShell>
      <h1 className="font-display text-2xl font-bold text-brand-deep">
        {t("blogc.errorTitle")}
      </h1>
      <p className="mt-3 text-muted-foreground">
        {t("blogc.errorBody")}{" "}
        <Link to="/blog" className="text-brand underline">
          {t("blogc.clinicalLibraryLink")}
        </Link>
        .
      </p>
    </ArticleShell>
  );
}

function BlogNotFoundShell() {
  const t = useT();
  return (
    <ArticleShell>
      <h1 className="font-display text-2xl font-bold text-brand-deep">{t("blogc.notFoundTitle")}</h1>
      <p className="mt-3 text-muted-foreground">
        {t("blogc.notFoundBody")}{" "}
        <Link to="/blog" className="text-brand underline">
          {t("blogc.clinicalLibraryLink")}
        </Link>
        .
      </p>
    </ArticleShell>
  );
}

function ArticleShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background pb-24 pt-28 md:pt-36">
      <article className="mx-auto max-w-3xl px-4 md:px-8">{children}</article>
    </div>
  );
}

function CmsArticle() {
  const t = useT();
  const post = Route.useLoaderData() as PublicPost;

  return (
    <ArticleShell>
      <Reveal>
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t("blogc.clinicalLibraryLink")}
        </Link>
        <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          {post.category}
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
          {post.title}
        </h1>
        {post.excerpt ? (
          <p className="mt-4 text-base text-muted-foreground md:text-lg">{post.excerpt}</p>
        ) : null}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <time dateTime={post.publishedAt}>
            {new Date(post.publishedAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </time>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {post.readingTime}
          </span>
          <span aria-hidden>·</span>
          {publicAuthorUrl(post.authorUrl) ? (
            <a href={publicAuthorUrl(post.authorUrl)!} className="underline">
              {publicAuthorName(post.authorName)}
            </a>
          ) : (
            <span>{publicAuthorName(post.authorName)}</span>
          )}
          {post.reviewerName ? (
            <>
              <span aria-hidden>·</span>
              <span>
                {t("blogc.medicallyReviewedBy")}{" "}
                {post.reviewerUrl ? (
                  <a href={post.reviewerUrl} className="underline">
                    {post.reviewerName}
                  </a>
                ) : (
                  post.reviewerName
                )}
                {post.reviewerCredentials ? `, ${post.reviewerCredentials}` : ""}
                {post.reviewedAt
                  ? ` · ${new Date(post.reviewedAt).toLocaleDateString("en-IN", {
                      month: "long",
                      year: "numeric",
                    })}`
                  : ""}
              </span>
            </>
          ) : null}
        </div>
      </Reveal>

      {post.heroImageUrl ? (
        <img
          src={sizedImageUrl(post.heroImageUrl, 960)}
          srcSet={imageSrcSet(post.heroImageUrl)}
          sizes="(min-width: 768px) 720px, 100vw"
          alt={post.heroImageAlt ?? post.title}
          loading="lazy"
          decoding="async"
          className="mt-8 w-full rounded-3xl border border-border object-cover"
        />
      ) : null}


      {post.keyTakeaways.length ? (
        <section className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-base font-bold text-brand-deep">
            {t("blogc.keyTakeaways")}
          </h2>
          <ul className="mt-3 space-y-2">
            {post.keyTakeaways.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
                <span aria-hidden className="text-brand">
                  •
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <BlockRenderer blocks={post.body} />

      {post.cluster && post.cluster in CLUSTERS ? (
        <ClinicalProductPitch cluster={post.cluster as ClusterKey} />
      ) : null}

      {post.faqs.length ? (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
            {t("blogc.faqHeading")}
          </h2>
          <div className="mt-4 space-y-3">
            {post.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {post.references.length ? (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
            {t("blogc.referencesHeading")}
          </h2>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            {post.references.map((r, i) => (
              <li key={i}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="underline"
                >
                  {r.label}
                </a>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <footer className="mt-14 rounded-2xl border border-border bg-card p-5 text-xs text-muted-foreground">
        {t("blogc.footerDisclaimer")}{" "}
        <a href={`${SITE}/blog/${post.slug}`} className="underline">
          truwe.in
        </a>
        .
      </footer>
    </ArticleShell>
  );
}
